import PrincipalBanner from "../../contents/PrincipalBanner";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

const Page = () => {
  return (
    <main className="mx-auto max-w-7xl">
      <PrincipalBanner
        
        classNames={twMerge(clsx("min-h-[200px] md:min-h-[490px] p-0"))}
      >
        <img
          className="absolute min-w-full min-h-full object-cover"
          src=" https://static.igem.wiki/teams/5058/novas-imagens/banner-novo-safety.webp "
          alt="bannner safety"
        />
        <h1 className="invisible">Safety</h1>
      </PrincipalBanner>

      <section className="pt-6 font-nunito">
        <h2 className="embadge">OVERVIEW</h2>
        <p className="px-6 pb-6 font-manjari text-3xl leading-normal">
          In this project, our goal is to engineer a new strain of yeast that
          produces more NR in comparison with the wild type. Therefore, we had
          to analyze different safety aspects in order to ensure that our
          project is harmless to humans, agriculture and the environment,
          discussed here. Moreover, we describe our implemented biocontainment
          strategies, developed to minimize spreading risks and address safety
          protocols we follow, such as personal protective equipment usage,
          proper chemical handling and waste disposal.
        </p>
      </section>

      <section className="">
        <h2 className={"title"}>PROJECT SAFETY</h2>
        <article>
          <h3>Application in agriculture</h3>
          <p>
            Once developed and made available, our product will serve as a
            bio-input for agriculture. It has the potential to benefit plants by
            boosting their natural defenses against environmental stresses and
            pathogens, leading to increased crop productivity. YOB3 can be
            utilized in industrial manufacturing, agriculture, and the food
            industry.
          </p>
          <p>
            We conducted interviews to analyze the risks of potential bad
            outcomes. We studied the possibility of harm to human health and
            safety, particularly since our project will be applied in rice
            agriculture. Moreover, we also examined the possible harm to the
            environment, as well as to agricultural animals, crops and
            domesticated animals.
          </p>
          <p>
            After analyzing and discussing with stakeholders and experts from
            different fields related to our project, we concluded that YOB3
            could pose a potential risk to crops or animals if a horizontal gene
            transfer occurs, such as resistance to antibiotics. However, our
            product should not be released to nature until all the modifications
            are done with CRISPR-Cas techniques, to avoid using antibiotic
            markers. As a supplement, dosing the quantity of the product is
            critical; overdosing could result in localized soil disturbances and
            also in outgrow of different pathogens, as NR is a supplement for
            all kinds of life.
          </p>
          <p>
            In terms of spreading risks, we have developed biocontainment
            strategies, which are described in the "Safety Design" section
            below.
          </p>
        </article>

        <article>
          <h3>Project biosafety level</h3>
          <p>
            Our lab work was done with three different strains of E. coli
            (DH5-alpha, HB101, BL21-DE3) and three of Saccharomyces cerevisiae
            (BY4741, BY4742, W303-a). These species are present in iGEM’s white
            list and considered safe for a biosafety level 1 laboratory. The
            vectors we used are all known and considered non-hazardous, which
            means that they are also considered safe for a biosafety level 1
            laboratory. For the inserts, we used yGFP, which is regarded as
            safe, TIR1, LACZ, FLO1 and NUCA, thus proper for a biosafety level 1
            laboratory.
          </p>
        </article>

        <article>
          <h3>Safety design</h3>
          <small>Non-pathogenic chassis</small>
          <p>
            For this product, we chose Saccharomyces cerevisiae as our chassis.
            This microorganism is widely used in various industrial processes,
            including beverage production, like wine and beer. In Brazil it is
            largely used for bioethanol production. It is not classified as
            pathogenic, and there have been no reported cases of S. cerevisiae
            isolates producing toxins harmful to humans or animals. It is listed
            on iGEM’s white list.
          </p>
          <p>
            Since our yeast strain is genetically modified, we need to test if
            it is harmful to animals or beneficial for the growth of pathogens
            before we register the product at the Ministry of Agriculture and
            Livestock (MAPA).
          </p>

          <small>Biocontainment strategies</small>
          <p>
            As a genetically modified organism, it is important to prevent its
            spread in nature. To do that, we have developed three different
            models of safety control:
            <ul className="text-sm ">
              <li>
                1.Auxotrophy to aminoacids: our strain is auxotrophic to some
                aminoacids, which means it is unlikely for it to survive outside
                controlled environment.
              </li>
              <li>
                2.Kill switch with nuclease: we designed a kill switch mechanism
                using a nuclease that will degrade RNA and DNA. Also we will
                degrade PHO85 kinase that when inhibited, progression through
                cell cycle and growth are reduced.{" "}
              </li>
              <li>
                3.Heat-killed inoculum: all of the inoculum will undergo heat
                treatment to kill the yeast cells before application of the
                product.For this we have used heat shock promoters to activate
                the nuclease.{" "}
              </li>
            </ul>
          </p>

          <small>Heat-Shock Induced Kill Switch</small>
          <p>
            In order to prevent our engineered yeast from spreading into the
            environment, we designed a heat-shock induced kill switch, which is
            based on the activity of the enzyme Nuclease A. It is designed to
            activate under specific conditions to trigger cell death.
          </p>
          <p>
            In this kill switch, a heat shock promoter is linked to the nucA
            gene, which encodes a nuclease, an enzyme that is capable of
            degrading cellular DNA and RNA. Under normal conditions, the
            promoter is inactive. However, when it undergoes heat shock, the
            promoter is activated and induces the expression of the nucA gene.
            This leads to the production of the enzyme, which degrades de cell
            DNA, causing cell death.
          </p>
        </article>
      </section>
      <section className="py-0 pb-4">
        <h2 className="title">LABORATORY SAFETY PROCEDURES</h2>
        <p>
          Safety procedures are a crucial part of our project, both for the team
          and the environment. We follow safety measures which help mitigate
          potential risks. These include the use of personal protective
          equipment, proper handling of chemicals and correct waste disposal
          procedures, as described below.
        </p>
        <p>
          Lab configuration Our experiments are conducted in a laboratory in
          UFRJ. It is equipped with a laminar flow hood, a chemical fume hood
          and all necessary safety equipment, such as personal protection, fire
          extinguishers. In addition, outside the lab we have an emergency
          shower. The university also has a biosafety and a firefighter
          department.
        </p>

        <h3>Personal Protective Equipment (PPE)</h3>
        <p>
          In the laboratory space, the students need to follow safety protocols.
          These include wearing appropriate PPE, such as lab coats and gloves,
          not wearing open shoes and tying up our hair. These measures help
          minimizing risks of contamination and ensure safety of the students.
        </p>

        <h3>Chemical Handling</h3>
        <p>
          In our project, we use hazardous chemicals which may have potential
          risks to human health if mishandled. We were instructed on the correct
          procedures for handling these substances safely. The laboratory is
          also equipped with a chemical fume hood to ensure proper ventilation
          during their use.
        </p>

        <h3>Waste Disposal</h3>
        <p>
          Our team has been trained in proper waste disposal practices. We
          follow the biosafety department's rules, which include decontaminating
          waste and discarding correctly materials that have come into direct
          contact with microbiological agents.
        </p>
      </section>
    </main>
  );
};
export default Page;
